/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {useSelector} from 'react-redux';
import {makeStyles} from '@material-ui/core';
import {SensorUnits} from '@ion/core/src/types/entities';
import {getAssetDetailsContentStyles} from '@ion/dls/src/components/assetdetails/current/assetDetailsContentStyles';
import {themedClassName} from '@ion/dls/src/theme/helpers';
import {dlsColor, dlsTypography} from '@ion/dls/src/theme/theme';
import {isCurrentTheme} from '@ion/dls/src/theme/useThemeContext';
import {DLSTheme} from '@ion/dls/src/types/types';
import {getFormattedDate} from '@ion/dls/src/utils/ComponentUtils';
import {systemOfUnitsSelector} from '@/store/selectors/login-selectors';
import {getTemperatureDisplayUnit} from './utils';

export interface TooltipDataType {
  eventTime: string;
  y: number;
  settings: {
    minTemperature?: number;
    maxTemperature?: number;
    minLightLevel?: number;
    maxLightLevel?: number;
    maxImpact?: number;
  }
  systemOfUnits?: {
    value: string;
  }
  units: string;
}
export interface GenericSensorTooltipProps {
  point: {
    data: TooltipDataType
  };
}

const useStyles = makeStyles((theme: DLSTheme) => {
  const contentStyles = getAssetDetailsContentStyles();
  return {
    tooltipContainer : {
      display       : 'flex',
      flexDirection : 'column',
      paddingRight  : '20px'
    },
    text : {
      font       : dlsTypography('body3'),
      color      : dlsColor('black'),
      fontSize   : 14,
      fontWeight : 500,
      padding    : '1px 0'
    },
    // current theme's shared "inline value" text style (Inter, grey2),
    // matching the Asset Details Drawer's other designed sections ...
    currentText : {
      ...contentStyles.text,
      padding : '1px 0'
    },
    secondaryText : {
      font       : dlsTypography('body3'),
      color      : dlsColor('grey3'),
      fontSize   : 10,
      fontWeight : 500,
      padding    : '1px 0'
    },
    // current theme's shared "meta" text style (Inter, grey2), matching
    // e.g. a last report time elsewhere in the drawer ...
    currentSecondaryText : {
      ...contentStyles.meta,
      padding : '1px 0'
    }
  };
});

/**
 * GenericSensorTooltip is a component used for displaying current data being hovered over in a chart/graph.
 *
 * ```typescript
 *  import {GenericSensorTooltip} from '@/ion-suite/src/routes/tracking/scDetails/reports';
 *
 *   <SomeComponent
 *     ...
 *     customToolTip={GenericSensorTooltip}/>
 * ```
 */
export const GenericSensorTooltip = ({point: {data}}: GenericSensorTooltipProps) => {
  const classes                = useStyles();
  const isCurrent              = isCurrentTheme();
  const systemOfUnits          = useSelector(systemOfUnitsSelector);
  const unit                   = data?.units;
  const celsius                = SensorUnits.C;
  const fahrenheit             = SensorUnits.F;
  const isTemperature          = unit === fahrenheit || unit === celsius;
  const temperatureDisplayUnit = getTemperatureDisplayUnit(unit as SensorUnits, systemOfUnits?.value);
  return (
    <div className={classes.tooltipContainer}>
      <span className={themedClassName(classes.text, classes.currentText, isCurrent)}>
        {data?.y}
        {isTemperature ? '°' : null} &nbsp;{isTemperature ? temperatureDisplayUnit : data?.units}
      </span>
      <span className={themedClassName(classes.secondaryText, classes.currentSecondaryText, isCurrent)}>
        {getFormattedDate(data?.eventTime)}
      </span>
    </div>
  );
};
